import React, { useEffect, useRef, useState } from "react";
import { PlateTypeCaptureProfile } from "../../../Model/CaptureProfile";
import { Coordinate2D } from "../../../Model/Coordinate";
import {
  CanvasSize,
  EMaskHandle,
  EMaskShape,
  EMaskShapeExtended,
  ImageMask,
  ImageMaskInPixel,
  MaskBounding,
} from "../../../Model/ImageMask";
import { ImagerState } from "../../../Model/ModuleState";
import { useTypedSelector } from "../../../redux/reducers/RootReducer";
import { getModuleStateById } from "../../../redux/reducers/ModuleStateReducer";

const dist2Point = (point1: Coordinate2D, point2: Coordinate2D) => {
  return Math.sqrt(
    (point1.x - point2.x) * (point1.x - point2.x) +
      (point1.y - point2.y) * (point1.y - point2.y)
  );
};

const isInsideRect = (point: Coordinate2D, bounding: MaskBounding) => {
  const halfWidth = bounding.width / 2;
  const halfHeight = bounding.height / 2;
  const topLeft = {
    x: bounding.centerPoint.x - halfWidth,
    y: bounding.centerPoint.y - halfHeight,
  };
  const topRight = {
    x: bounding.centerPoint.x + halfWidth,
    y: bounding.centerPoint.y - halfHeight,
  };
  const bottomRight = {
    x: bounding.centerPoint.x + halfWidth,
    y: bounding.centerPoint.y + halfHeight,
  };
  const bottomLeft = {
    x: bounding.centerPoint.x - halfWidth,
    y: bounding.centerPoint.y + halfHeight,
  };
  return (
    point.x > topLeft.x &&
    point.x < topRight.x &&
    point.y > topLeft.y &&
    point.y < bottomLeft.y
  );
};

const isInsideEllipse = (point: Coordinate2D, bounding: MaskBounding) => {
  return (
    (point.x - bounding.centerPoint.x) ** 2 / (bounding.width / 2) ** 2 +
      (point.y - bounding.centerPoint.y) ** 2 / (bounding.height / 2) ** 2 <=
    1
  );
};

const isInsideMaskBody = (
  point: Coordinate2D,
  canvas: HTMLCanvasElement,
  mask: ImageMaskInPixel
) => {
  const maskBounding: MaskBounding = {
    centerPoint: {
      x: canvas.width / 2 + mask.centerXOffset,
      y: canvas.height / 2 + mask.centerYOffset,
    },
    width: mask.width,
    height: mask.height,
  };

  switch (mask.shape) {
    case EMaskShape.Ellipse:
      return isInsideEllipse(point, maskBounding);
    case EMaskShape.Rectangle:
    case EMaskShape.RoundedRectangle:
      return isInsideRect(point, maskBounding);
    default:
      return false;
  }
};

const getHandlePoint = (canvas: HTMLCanvasElement, mask: ImageMaskInPixel) => {
  const halfWidth = mask.width / 2;
  const halfHeight = mask.height / 2;
  const centerPoint = {
    x: canvas.width / 2 + mask.centerXOffset,
    y: canvas.height / 2 + mask.centerYOffset,
  };

  const topLeftPoint = {
    x: centerPoint.x - halfWidth,
    y: centerPoint.y - halfHeight,
  };

  const handleTop = {
    x: centerPoint.x,
    y: centerPoint.y - halfHeight,
  };

  const handleRight = {
    x: centerPoint.x + halfWidth,
    y: centerPoint.y,
  };

  const handleBottom = {
    x: centerPoint.x,
    y: centerPoint.y + halfHeight,
  };

  const handleLeft = {
    x: centerPoint.x - halfWidth,
    y: centerPoint.y,
  };

  const handleRound = {
    x: topLeftPoint.x + mask.radius,
    y: topLeftPoint.y,
  };

  return [handleTop, handleRight, handleBottom, handleLeft, handleRound];
};

const drawRect = (
  canvas: HTMLCanvasElement,
  mask: ImageMaskInPixel,
  handleRadius: number,
  disabled: boolean
) => {
  const ctx = canvas.getContext("2d");
  const halfWidth = mask.width / 2;
  const halfHeight = mask.height / 2;
  const centerPoint = {
    x: canvas.width / 2 + mask.centerXOffset,
    y: canvas.height / 2 + mask.centerYOffset,
  };

  const topLeftRect = {
    x: centerPoint.x - halfWidth,
    y: centerPoint.y - halfHeight,
  };

  const [handleTop, handleRight, handleBottom, handleLeft] = getHandlePoint(
    canvas,
    mask
  );

  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
    ctx.fill();
    ctx.closePath();

    ctx.beginPath();
    ctx.rect(topLeftRect.x, topLeftRect.y, mask.width, mask.height);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
    ctx.stroke();
    ctx.closePath();

    ctx.clearRect(topLeftRect.x, topLeftRect.y, mask.width, mask.height);
    if (!disabled) {
      ctx.fillStyle = "rgba(247, 162, 70,1)";
      ctx.strokeStyle = "rgba(247, 162, 70,1)";

      ctx.beginPath();
      ctx.arc(handleTop.x, handleTop.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleRight.x, handleRight.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleBottom.x, handleBottom.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleLeft.x, handleLeft.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();
    }
  }
};

const drawRoundRect = (
  canvas: HTMLCanvasElement,
  mask: ImageMaskInPixel,
  handleRadius: number,
  disabled: boolean
) => {
  const ctx = canvas.getContext("2d");
  const halfWidth = mask.width / 2;
  const halfHeight = mask.height / 2;
  const centerPoint = {
    x: canvas.width / 2 + mask.centerXOffset,
    y: canvas.height / 2 + mask.centerYOffset,
  };

  const topLeftRect = {
    x: centerPoint.x - halfWidth,
    y: centerPoint.y - halfHeight,
  };

  const [handleTop, handleRight, handleBottom, handleLeft, handleRound] =
    getHandlePoint(canvas, mask);

  if (ctx) {
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.width);
    ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
    ctx.fill();
    ctx.closePath();

    ctx.beginPath();
    ctx.moveTo(topLeftRect.x + mask.radius, topLeftRect.y);
    ctx.lineTo(topLeftRect.x + mask.width - mask.radius, topLeftRect.y);
    ctx.quadraticCurveTo(
      topLeftRect.x + mask.width,
      topLeftRect.y,
      topLeftRect.x + mask.width,
      topLeftRect.y + mask.radius
    );
    ctx.lineTo(
      topLeftRect.x + mask.width,
      topLeftRect.y + mask.height - mask.radius
    );
    ctx.quadraticCurveTo(
      topLeftRect.x + mask.width,
      topLeftRect.y + mask.height,
      topLeftRect.x + mask.width - mask.radius,
      topLeftRect.y + mask.height
    );
    ctx.lineTo(topLeftRect.x + mask.radius, topLeftRect.y + mask.height);
    ctx.quadraticCurveTo(
      topLeftRect.x,
      topLeftRect.y + mask.height,
      topLeftRect.x,
      topLeftRect.y + mask.height - mask.radius
    );
    ctx.lineTo(topLeftRect.x, topLeftRect.y + mask.radius);
    ctx.quadraticCurveTo(
      topLeftRect.x,
      topLeftRect.y,
      topLeftRect.x + mask.radius,
      topLeftRect.y
    );
    ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
    ctx.stroke();
    ctx.clip();
    ctx.clearRect(0, 0, canvas.width, canvas.width);
    ctx.closePath();

    ctx.restore();

    if (!disabled) {
      ctx.fillStyle = "rgba(247, 162, 70,1)";
      ctx.strokeStyle = "rgba(247, 162, 70,1)";
      ctx.beginPath();
      ctx.arc(handleTop.x, handleTop.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleRight.x, handleRight.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleBottom.x, handleBottom.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleLeft.x, handleLeft.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleRound.x, handleRound.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();
    }
  }
};

const drawEllipse = (
  canvas: HTMLCanvasElement,
  mask: ImageMaskInPixel,
  handleRadius: number,
  disabled: boolean
) => {
  const ctx = canvas.getContext("2d");
  const centerPoint = {
    x: canvas.width / 2 + mask.centerXOffset,
    y: canvas.height / 2 + mask.centerYOffset,
  };

  const [handleTop, handleRight, handleBottom, handleLeft] = getHandlePoint(
    canvas,
    mask
  );

  if (ctx) {
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    ctx.rect(0, 0, canvas.width, canvas.width);
    ctx.fillStyle = "rgba(0, 0, 0, 0.75)";
    ctx.fill();
    ctx.closePath();

    ctx.beginPath();
    ctx.ellipse(
      centerPoint.x,
      centerPoint.y,
      mask.width / 2,
      mask.height / 2,
      Math.PI,
      0,
      2 * Math.PI
    );
    ctx.strokeStyle = "rgba(0, 0, 0, 0.75)";
    ctx.stroke();
    ctx.clip();
    ctx.clearRect(0, 0, canvas.width, canvas.width);
    ctx.closePath();

    ctx.restore();

    if (!disabled) {
      ctx.fillStyle = "rgba(247, 162, 70,1)";
      ctx.strokeStyle = "rgba(247, 162, 70,1)";
      ctx.beginPath();
      ctx.arc(handleTop.x, handleTop.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleRight.x, handleRight.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleBottom.x, handleBottom.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();

      ctx.beginPath();
      ctx.arc(handleLeft.x, handleLeft.y, handleRadius, 0, 2 * Math.PI);
      ctx.stroke();
      ctx.fill();
      ctx.closePath();
    }
  }
};

const drawMask = (
  canvas: HTMLCanvasElement,
  mask: ImageMaskInPixel,
  handleRadius: number,
  disabled: boolean
) => {
  switch (mask.shape) {
    case EMaskShape.Ellipse:
      drawEllipse(canvas, mask, handleRadius, disabled);
      break;
    case EMaskShape.Rectangle:
      drawRect(canvas, mask, handleRadius, disabled);
      break;
    case EMaskShape.RoundedRectangle:
      drawRoundRect(canvas, mask, handleRadius, disabled);
      break;
    default:
      break;
  }
};

function getMaskShape(extendedMaskShape: EMaskShapeExtended) {
  switch (extendedMaskShape) {
    case EMaskShapeExtended.Ellipse:
    case EMaskShapeExtended.Circle:
      return EMaskShape.Ellipse;
    case EMaskShapeExtended.Rectangle:
    case EMaskShapeExtended.Square:
      return EMaskShape.Rectangle;
    case EMaskShapeExtended.RoundedRectangle:
    case EMaskShapeExtended.RoundedSquare:
      return EMaskShape.RoundedRectangle;
    default:
      return EMaskShape.Ellipse;
  }
}

function getImageMaskInPixel(
  imageMask: ImageMask | undefined | null,
  extendedMaskShape: EMaskShapeExtended,
  imagerState: ImagerState,
  canvasSize : CanvasSize
): ImageMaskInPixel {
  if (imageMask == null) {
    return {
      shape: getMaskShape(extendedMaskShape),
      width: 200,
      height: 200,
      centerXOffset: 0,
      centerYOffset: 0,
      radius: 0,
    };
  }

  const pixelSize = (imagerState.fovX / imagerState.imageWidth);

  const calculatedWith = imageMask.widthInMicrons / pixelSize;
  const calculatedHeight = imageMask.heightInMicrons / pixelSize;

  const maskMaxWidth = canvasSize.width;
  const maskMaxHeigh = canvasSize.height;

  return {
    shape: imageMask.shape,
    width: calculatedWith > maskMaxWidth? maskMaxWidth: calculatedWith, //mask size should not larger then the canvas size
    height: calculatedHeight > maskMaxHeigh ? maskMaxHeigh : calculatedHeight,//mask size should not larger then the canvas size
    centerXOffset: imageMask.centerXOffsetInMicrons / pixelSize,
    centerYOffset: imageMask.centerYOffsetInMicrons / pixelSize,
    radius: imageMask.radiusForRoundedEdgesInMicrons / pixelSize,
  };
}

function getImageMask(
  imageMaskInPixel: ImageMaskInPixel,
  imagerState: ImagerState
): ImageMask {
  const pixelSize = imagerState.fovX / imagerState.imageWidth;
  return {
    shape: imageMaskInPixel.shape,
    widthInMicrons: imageMaskInPixel.width * pixelSize,
    heightInMicrons: imageMaskInPixel.height * pixelSize,
    centerXOffsetInMicrons: imageMaskInPixel.centerXOffset * pixelSize,
    centerYOffsetInMicrons: imageMaskInPixel.centerYOffset * pixelSize,
    radiusForRoundedEdgesInMicrons: imageMaskInPixel.radius * pixelSize,
  };
}

type Props = {
  extendedMaskShape: EMaskShapeExtended;
  imagerIdentifier: string;
  container?: React.RefObject<HTMLDivElement>;
  captureProfile: PlateTypeCaptureProfile;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
};

export default function ImageMaskCanvas({
  extendedMaskShape,
  imagerIdentifier,
  container,
  captureProfile,
  setCaptureProfile,
}: Props) {
  const imagerState = useTypedSelector(
    (state) => getModuleStateById(state)[imagerIdentifier]?.imager
  );


  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const canvas = useRef<HTMLCanvasElement>(null);
  const background = useRef<HTMLDivElement>(null);
  const minMaskHeight = 50;
  const minMaskWidth = 50;
  const handleRadius = 6;

  const [canvasSize, setCanvasSize] = useState({width:100,height:100} as CanvasSize);

  let mask = getImageMaskInPixel(
    captureProfile.imageMask,
    extendedMaskShape,
    imagerState,
    canvasSize
  );
  let isDrag = useRef(false);
  let activeHandle = EMaskHandle.NONE;
  let cursorLastPos = { x: 0, y: 0 };
  let disabled = captureProfile.plateMaskEnabled ? false : true;


  useEffect(() => {
    const mouseLeave = (event: MouseEvent) => {
      isDrag.current = false;
      activeHandle = EMaskHandle.NONE;
    };
    if (canvas.current) {
      canvas.current.addEventListener("mouseleave", mouseLeave);
    }
    return () => {
      if (canvas.current) {
        canvas.current.removeEventListener("mouseleave", mouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    const currentBackground = container
      ? container.current
      : background.current;
    if (canvas.current && currentBackground) {
      const displayWidth = currentBackground.clientWidth;
      const displayHeight = currentBackground.clientHeight;
      const ratio = Math.min(
        displayWidth / imagerState.imageWidth,
        displayHeight / imagerState.imageHeight
      );

      const scaledWidth = imagerState.imageWidth * ratio;
      const scaledHeight = imagerState.imageHeight * ratio;

      canvas.current.width = scaledWidth;
      canvas.current.height = scaledHeight;

      setCanvasSize({
        width : canvas.current.width,
        height : canvas.current.height
      })

      canvas.current.style.left = 0.5 * (displayWidth - scaledWidth) + "px";
      drawMask(canvas.current, mask, handleRadius, disabled);
    }
  }, [captureProfile, imagerState.imageWidth, imagerState.imageHeight, container, disabled]);

  // useEffect(() => {
  //   if (canvas.current) {
  //     drawMask(canvas.current, mask, handleRadius, disabled);
  //   }
  // }, [captureProfile]);

  const getActiveHandle = (cursorPos: Coordinate2D) => {
    if (canvas.current) {
      const [handleTop, handleRight, handleBottom, handleLeft, handleRound] =
        getHandlePoint(canvas.current, mask);

      if (dist2Point(cursorPos, handleTop) <= handleRadius) {
        return EMaskHandle.TOP;
      } else if (dist2Point(cursorPos, handleRight) <= handleRadius) {
        return EMaskHandle.RIGHT;
      } else if (dist2Point(cursorPos, handleBottom) <= handleRadius) {
        return EMaskHandle.BOTTOM;
      } else if (dist2Point(cursorPos, handleLeft) <= handleRadius) {
        return EMaskHandle.LEFT;
      } else if (dist2Point(cursorPos, handleRound) <= handleRadius) {
        return EMaskHandle.ROUND;
      } else if (isInsideMaskBody(cursorPos, canvas.current, mask)) {
        return EMaskHandle.BODY;
      } else {
        return EMaskHandle.NONE;
      }
    } else {
      return EMaskHandle.NONE;
    }
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    e.preventDefault();
    const currentBackground = container
      ? container.current
      : background.current;
    const offset: Coordinate2D = {
      x: canvas.current?.offsetLeft || 0,
      y: canvas.current?.offsetTop || 0,
    };

    offset.x += currentBackground?.offsetLeft || 0;
    offset.y += currentBackground?.offsetTop || 0;

    const cursorPos: Coordinate2D = {
      x: e.pageX - offset.x,
      y: e.pageY - offset.y,
    };

    const handle: EMaskHandle = getActiveHandle(cursorPos);
    activeHandle = !disabled ? handle : EMaskHandle.NONE;
    isDrag.current = true;
    cursorLastPos = cursorPos;
  };

  const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    activeHandle = EMaskHandle.NONE;
    isDrag.current = false;
    if (!disabled) {
      setCaptureProfile({
        ...captureProfile,
        imageMask: getImageMask(mask, imagerState),
      });
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
    const currentBackground = container
      ? container.current
      : background.current;
    const equalMaskWidthHeight =
      extendedMaskShape === EMaskShapeExtended.Circle ||
      extendedMaskShape === EMaskShapeExtended.Square ||
      extendedMaskShape === EMaskShapeExtended.RoundedSquare;
    e.preventDefault();

    if (
      isDrag.current &&
      activeHandle !== EMaskHandle.NONE &&
      canvas?.current &&
      !disabled
    ) {
      const offset: Coordinate2D = {
        x: canvas.current?.offsetLeft || 0,
        y: canvas.current?.offsetTop || 0,
      };

      offset.x += currentBackground?.offsetLeft || 0;
      offset.y += currentBackground?.offsetTop || 0;

      const cursorPos: Coordinate2D = {
        x: e.pageX - offset.x,
        y: e.pageY - offset.y,
      };

      const cursorDelta: Coordinate2D = {
        x: cursorPos.x - cursorLastPos.x,
        y: cursorPos.y - cursorLastPos.y,
      };

      let newMask = {
        ...mask,
      };

      switch (activeHandle) {
        case EMaskHandle.TOP:
          newMask.height -= cursorDelta.y * 2;
          if (equalMaskWidthHeight) {
            newMask.width -= cursorDelta.y * 2;
          }
          if (newMask.radius > newMask.height / 4 && newMask.radius > 0) {
            newMask.radius -= cursorDelta.y;
          }
          break;
        case EMaskHandle.BOTTOM:
          newMask.height += cursorDelta.y * 2;
          if (equalMaskWidthHeight) {
            newMask.width += cursorDelta.y * 2;
          }
          if (newMask.radius > newMask.height / 4 && newMask.radius > 0) {
            newMask.radius += cursorDelta.y;
          }
          break;
        case EMaskHandle.LEFT:
          newMask.width -= cursorDelta.x * 2;
          if (equalMaskWidthHeight) {
            newMask.height -= cursorDelta.x * 2;
          }
          if (newMask.radius > newMask.width / 4 && newMask.radius > 0) {
            newMask.radius -= cursorDelta.x;
          }
          break;
        case EMaskHandle.RIGHT:
          newMask.width += cursorDelta.x * 2;
          if (equalMaskWidthHeight) {
            newMask.height += cursorDelta.x * 2;
          }
          if (newMask.radius > newMask.width / 4 && newMask.radius > 0) {
            newMask.radius += cursorDelta.x;
          }
          break;
        case EMaskHandle.ROUND:
          newMask.radius += cursorDelta.x;
          break;
        case EMaskHandle.BODY:
          newMask.centerXOffset += cursorDelta.x;
          newMask.centerYOffset += cursorDelta.y;
          break;
        default:
          break;
      }

      if (
        newMask.width >= minMaskWidth &&
        newMask.height >= minMaskHeight &&
        newMask.radius < Math.min(newMask.width / 4, newMask.height / 4) &&
        newMask.radius >= 0
      ) {
        mask = newMask;
      }

      drawMask(canvas.current, mask, handleRadius, disabled);

      cursorLastPos = cursorPos;
    }
  };

  console.log("IMAGEMASKCANVAS RENDERED");

  return (
    <div
      ref={background}
      style={{
        position: container ? "absolute" : "relative",
        height: "100%",
        width: "100%",
        overflow: "hidden",
        top: 0,
      }}
    >
      <canvas
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        ref={canvas}
        style={{
          position: "absolute",
          top: "auto",
          left: "auto",
          bottom: "auto",
          right: "auto",
          zIndex: 10,
        }}
      />
    </div>
  );
}
